/**
 * O registro de acesso a prontuário (regra inviolável 7).
 *
 * O Postgres não tem gatilho de leitura, então quem lê avisa: o adaptador chama
 * `registrar_acesso_ao_prontuario` junto com a leitura, e a leitura FALHA se o
 * registro falhar. Engolir o erro aqui seria reabrir, calado, o prontuário sem
 * rastro que isto existe para fechar.
 *
 * Fica fora do adaptador para a regra ser testada sem servidor.
 */

export type RecursoDoProntuario = 'prontuario' | 'paciente' | 'anexo';

export interface ArgumentosDoRegistro {
    p_contato: string;
    p_recurso: RecursoDoProntuario;
    p_referencia?: string;
}

export type ChamadaDoRegistro = (args: ArgumentosDoRegistro) => PromiseLike<{ error: { message: string; code?: string } | null }>;

export async function registrarAcessoAoProntuario(
    chamar: ChamadaDoRegistro,
    contatoId: string | null,
    recurso: RecursoDoProntuario,
    referencia?: string,
): Promise<void> {
    if (!contatoId) {
        throw new Error('O acesso ao prontuário não pôde ser registrado: o paciente não foi identificado.');
    }
    const { error } = await chamar({
        p_contato: contatoId,
        p_recurso: recurso,
        ...(referencia ? { p_referencia: referencia } : {}),
    });
    /* Paciente que não existe, ou de outra clínica, a função recusa como "fora do
       seu acesso". Para quem lê é 404, igual à RLS quando não enxerga a linha:
       dizer "sem permissão" contaria que o paciente existe em algum lugar. */
    if (error?.code === '42501' && /fora do seu acesso/i.test(error.message)) {
        throw new Response('Paciente não encontrado.', { status: 404, statusText: 'Paciente não encontrado' });
    }
    if (error) {
        throw new Error(`O acesso ao prontuário não pôde ser registrado, e por isso não foi aberto: ${error.message}`);
    }
}

/**
 * O paciente dono de um anexo, pelo caminho no balde.
 *
 * O caminho é `<clinica>/<contato>/<campo>/<arquivo>` (ver `enviarAnexoDaFicha`).
 * Caminho fora desse formato não tem dono conhecido — e anexo sem dono não se
 * abre, porque não há de quem registrar o acesso.
 */
export function contatoDoCaminhoDoAnexo(caminho: string): string | null {
    const partes = caminho.split('/');
    const contato = partes[1];
    if (partes.length < 4 || !contato) return null;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(contato) ? contato : null;
}
